/**
 * `/flashcards/$deckId` — a single flashcard deck (P-SCH-D).
 *
 * Two modes over the same deck:
 *   - **Edit** (default): the deck's cards from
 *     `learning.learner.flashcards.cards.list`, each with inline Q (front) /
 *     A (back), an edit dialog, and a delete. An "Add card" dialog creates
 *     one via `cards.create`.
 *   - **Study**: a flip self-test — one card at a time, click (or Space /
 *     Enter) to flip front↔back, Prev/Next to move, a shuffle, and progress.
 *
 * Cards are learner-OWNED (server enforces ownership through the deck); this
 * screen never sends an owner. `$…$` LaTeX on either face renders through
 * `<MathText>`. Mobile-first: the study card fills the column at phone width,
 * controls stack, and every tap target meets the 44px floor.
 */
import * as React from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import type { inferRouterOutputs } from '@trpc/server';
import {
  ArrowLeft,
  ChevronLeft,
  ChevronRight,
  Layers,
  Pencil,
  Plus,
  RotateCw,
  Shuffle,
  Trash2,
} from 'lucide-react';
import {
  Button,
  Card,
  CardContent,
  ConfirmDialog,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  EmptyState,
  Label,
  MathText,
  Skeleton,
  cn,
} from '@mangrove/ui';
import { trpc, type AppRouter } from '../lib/trpc.js';
import { RequireAuth } from '../lib/auth.js';
import { LearnShell } from '../components/shell.js';

export const Route = createFileRoute('/flashcards/$deckId')({
  component: DeckPage,
});

type FlashCard =
  inferRouterOutputs<AppRouter>['learning']['learner']['flashcards']['cards']['list'][number];

type ViewMode = 'edit' | 'study';

function DeckPage(): React.JSX.Element {
  return (
    <RequireAuth>
      <LearnShell title="Deck">
        <DeckContent />
      </LearnShell>
    </RequireAuth>
  );
}

function DeckContent(): React.JSX.Element {
  const { deckId } = Route.useParams();
  const cardsQ = trpc.learning.learner.flashcards.cards.list.useQuery({ deckId });
  const [mode, setMode] = React.useState<ViewMode>('edit');
  const [addOpen, setAddOpen] = React.useState(false);

  const cards = cardsQ.data ?? [];

  return (
    <div className="space-y-6">
      <div className="space-y-4">
        <Link
          to="/flashcards"
          className="inline-flex min-h-touch items-center gap-1.5 text-sm text-muted-foreground transition-colors hover:text-foreground"
          data-testid="back-to-decks"
        >
          <ArrowLeft className="size-4" aria-hidden="true" />
          Decks
        </Link>

        <div className="flex items-center gap-3">
          <span
            className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary"
            aria-hidden="true"
          >
            <Layers className="size-5" />
          </span>
          <div className="min-w-0">
            <h2 className="text-xl font-semibold tracking-tight">Deck</h2>
            <p className="text-sm text-muted-foreground tabular-nums">
              {cardsQ.isPending
                ? 'Loading cards…'
                : `${cards.length} ${cards.length === 1 ? 'card' : 'cards'}`}
            </p>
          </div>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="inline-flex rounded-lg border bg-background p-0.5" role="tablist">
            <button
              type="button"
              role="tab"
              aria-selected={mode === 'edit'}
              onClick={() => setMode('edit')}
              data-testid="mode-edit"
              className={cn(
                'press min-h-touch rounded-md px-4 py-1.5 text-sm font-medium transition-colors',
                mode === 'edit' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground',
              )}
            >
              Edit
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={mode === 'study'}
              onClick={() => setMode('study')}
              data-testid="mode-study"
              disabled={cards.length === 0}
              className={cn(
                'press min-h-touch rounded-md px-4 py-1.5 text-sm font-medium transition-colors disabled:opacity-50',
                mode === 'study' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground',
              )}
            >
              Study
            </button>
          </div>
          {mode === 'edit' ? (
            <Button
              onClick={() => setAddOpen(true)}
              className="press min-h-touch"
              data-testid="add-card"
            >
              <Plus className="size-4" aria-hidden="true" />
              Add card
            </Button>
          ) : null}
        </div>
      </div>

      {cardsQ.isPending ? (
        <div className="space-y-3">
          {Array.from({ length: 3 }, (_, i) => (
            <Skeleton key={i} className="h-20 w-full rounded-xl" />
          ))}
        </div>
      ) : cardsQ.error ? (
        <EmptyState
          icon={Layers}
          title="Couldn’t load this deck"
          description={cardsQ.error.message}
          action={
            <Button variant="outline" onClick={() => void cardsQ.refetch()}>
              Try again
            </Button>
          }
        />
      ) : cards.length === 0 ? (
        <EmptyState
          icon={Layers}
          title="No cards yet"
          description="Add your first card — a question on the front, the answer on the back."
          action={
            <Button onClick={() => setAddOpen(true)} className="press">
              <Plus className="size-4" aria-hidden="true" />
              Add card
            </Button>
          }
        />
      ) : mode === 'study' ? (
        <StudyMode cards={cards} />
      ) : (
        <ul className="ls-stagger space-y-3">
          {cards.map((card) => (
            <CardRow key={card.id} card={card} deckId={deckId} />
          ))}
        </ul>
      )}

      <CardEditorDialog deckId={deckId} open={addOpen} onOpenChange={setAddOpen} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Edit mode — card rows
// ---------------------------------------------------------------------------

function CardRow({ card, deckId }: { card: FlashCard; deckId: string }): React.JSX.Element {
  const utils = trpc.useUtils();
  const [editOpen, setEditOpen] = React.useState(false);
  const [confirmOpen, setConfirmOpen] = React.useState(false);
  const remove = trpc.learning.learner.flashcards.cards.delete.useMutation({
    onSuccess: () => {
      setConfirmOpen(false);
      void utils.learning.learner.flashcards.cards.list.invalidate({ deckId });
    },
  });

  return (
    <li>
      <Card data-testid="card-row" className="card-lift">
        <CardContent className="flex items-start justify-between gap-3 p-4">
          <div className="min-w-0 flex-1 space-y-2">
            <div className="flex gap-2 text-sm">
              <span className="shrink-0 font-semibold text-muted-foreground">Q</span>
              <div className="min-w-0 break-words text-foreground">
                <MathText text={card.front} />
              </div>
            </div>
            <div className="flex gap-2 text-sm">
              <span className="shrink-0 font-semibold text-muted-foreground">A</span>
              <div className="min-w-0 break-words text-muted-foreground">
                <MathText text={card.back} />
              </div>
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-1">
            <Button
              variant="ghost"
              size="icon"
              className="text-muted-foreground hover:text-foreground"
              aria-label="Edit card"
              data-testid="edit-card"
              onClick={() => setEditOpen(true)}
            >
              <Pencil className="size-4" aria-hidden="true" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="text-muted-foreground hover:text-destructive"
              aria-label="Delete card"
              data-testid="delete-card"
              onClick={() => setConfirmOpen(true)}
            >
              <Trash2 className="size-4" aria-hidden="true" />
            </Button>
          </div>
        </CardContent>
      </Card>

      <CardEditorDialog deckId={deckId} card={card} open={editOpen} onOpenChange={setEditOpen} />

      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={(open) => {
          if (!remove.isPending) setConfirmOpen(open);
        }}
        title="Delete this card?"
        description="This card will be permanently removed from the deck."
        confirmLabel="Delete card"
        cancelLabel="Keep card"
        destructive
        pending={remove.isPending}
        error={remove.error?.message ?? null}
        onConfirm={() => remove.mutate({ id: card.id })}
      />
    </li>
  );
}

/**
 * Add / edit a card. With `card` set it edits via `cards.update` (only the
 * changed `front`/`back`); without it, it creates via `cards.create`.
 */
function CardEditorDialog({
  deckId,
  card,
  open,
  onOpenChange,
}: {
  deckId: string;
  card?: FlashCard;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}): React.JSX.Element {
  const utils = trpc.useUtils();
  const isEdit = card != null;
  const [front, setFront] = React.useState('');
  const [back, setBack] = React.useState('');

  // Seed the fields each time the dialog opens (edit → existing, add → blank).
  React.useEffect(() => {
    if (open) {
      setFront(card?.front ?? '');
      setBack(card?.back ?? '');
    }
  }, [open, card]);

  const invalidate = (): void => {
    void utils.learning.learner.flashcards.cards.list.invalidate({ deckId });
    void utils.learning.learner.flashcards.decks.list.invalidate();
  };

  const create = trpc.learning.learner.flashcards.cards.create.useMutation({
    onSuccess: () => {
      invalidate();
      onOpenChange(false);
    },
  });
  const update = trpc.learning.learner.flashcards.cards.update.useMutation({
    onSuccess: () => {
      invalidate();
      onOpenChange(false);
    },
  });

  const pending = create.isPending || update.isPending;
  const error = create.error?.message ?? update.error?.message ?? null;
  const valid = front.trim() !== '' && back.trim() !== '';

  function handleSubmit(e: React.FormEvent): void {
    e.preventDefault();
    if (!valid || pending) return;
    if (isEdit && card) {
      update.mutate({ id: card.id, front: front.trim(), back: back.trim() });
    } else {
      create.mutate({ deckId, front: front.trim(), back: back.trim() });
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!pending) onOpenChange(next);
      }}
    >
      <DialogContent className="animate-scale-in">
        <form onSubmit={handleSubmit} className="space-y-4">
          <DialogHeader>
            <DialogTitle>{isEdit ? 'Edit card' : 'Add card'}</DialogTitle>
            <DialogDescription>
              Front is the question, back is the answer. Use $…$ for math.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-1.5">
            <Label htmlFor="card-front">Front (question)</Label>
            <textarea
              id="card-front"
              value={front}
              onChange={(e) => setFront(e.target.value)}
              placeholder="What is the capital of France?"
              data-testid="card-front"
              autoFocus
              className="flex min-h-20 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="card-back">Back (answer)</Label>
            <textarea
              id="card-back"
              value={back}
              onChange={(e) => setBack(e.target.value)}
              placeholder="Paris"
              data-testid="card-back"
              className="flex min-h-20 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            />
          </div>

          {error ? (
            <p className="text-sm text-destructive" role="alert">
              {error}
            </p>
          ) : null}

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={pending}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={!valid || pending}
              data-testid="card-save"
              className="press"
            >
              {pending ? 'Saving…' : isEdit ? 'Save card' : 'Add card'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Study mode — flip self-test
// ---------------------------------------------------------------------------

function StudyMode({ cards }: { cards: FlashCard[] }): React.JSX.Element {
  // A shuffleable order over indices into `cards`; reset when the deck changes.
  const [order, setOrder] = React.useState<number[]>(() => cards.map((_, i) => i));
  const [pos, setPos] = React.useState(0);
  const [flipped, setFlipped] = React.useState(false);

  React.useEffect(() => {
    setOrder(cards.map((_, i) => i));
    setPos(0);
    setFlipped(false);
  }, [cards]);

  // SCH-I3-J — tell Momentum this learner studied flashcards (server throttles
  // to once/day and 403s when gamification is off; failures are swallowed so a
  // rewards ping can never disturb studying).
  const pingStudy = trpc.learning.learner.gamification.recordActivity.useMutation({
    onError: () => undefined,
  });
  const pingedRef = React.useRef(false);
  React.useEffect(() => {
    if (pingedRef.current || cards.length === 0) return;
    pingedRef.current = true;
    pingStudy.mutate({ kind: 'flashcard.reviewed' });
  }, [cards, pingStudy]);

  const total = order.length;
  const card = cards[order[pos] ?? 0];
  if (!card || total === 0) return <></>;

  const go = (delta: number): void => {
    setFlipped(false);
    setPos((p) => Math.min(Math.max(p + delta, 0), total - 1));
  };

  const goTo = (next: number): void => {
    setFlipped(false);
    setPos(Math.min(Math.max(next, 0), total - 1));
  };

  const shuffle = (): void => {
    // Fisher–Yates over a copy of the current order.
    const next = order.slice();
    for (let i = next.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [next[i], next[j]] = [next[j] as number, next[i] as number];
    }
    setOrder(next);
    setPos(0);
    setFlipped(false);
  };

  const pct = Math.round(((pos + 1) / total) * 100);

  return (
    <div className="grid items-start gap-6 lg:grid-cols-[1fr_18rem]">
      {/* ---- study column ---- */}
      <div className="space-y-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <span className="text-sm font-medium text-muted-foreground tabular-nums">
            Card {pos + 1} of {total}
          </span>
          <Button
            variant="ghost"
            size="sm"
            onClick={shuffle}
            data-testid="shuffle"
            className="press min-h-touch"
          >
            <Shuffle className="size-4" aria-hidden="true" />
            Shuffle
          </Button>
        </div>

        {/* 3D flip card — front (card) / back (primary gradient) */}
        <div className="[perspective:1700px]">
          <button
            key={card.id}
            type="button"
            onClick={() => setFlipped((f) => !f)}
            data-testid="study-card"
            aria-pressed={flipped}
            aria-label={
              flipped
                ? 'Showing answer — tap to see the question'
                : 'Showing question — tap to reveal the answer'
            }
            className={cn(
              'animate-fade-in press relative block aspect-[8/5] w-full rounded-2xl transition-transform duration-500 [transform-style:preserve-3d] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
              flipped && '[transform:rotateY(180deg)]',
            )}
          >
            {/* front face */}
            <span className="card-soft absolute inset-0 flex flex-col items-center justify-center rounded-2xl border bg-card p-8 text-center [backface-visibility:hidden]">
              <span className="absolute left-5 top-5 inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
                <span className="size-1.5 rounded-full bg-primary" aria-hidden="true" />
                Question
              </span>
              <span className="max-w-prose text-2xl font-semibold leading-snug text-foreground sm:text-3xl">
                <MathText text={card.front} />
              </span>
              <span className="absolute bottom-5 right-5 inline-flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
                <RotateCw className="size-3.5" aria-hidden="true" />
                Tap to flip
              </span>
            </span>
            {/* back face */}
            <span className="absolute inset-0 flex flex-col items-center justify-center rounded-2xl bg-gradient-to-br from-primary to-brand-deep p-8 text-center text-primary-foreground shadow-lg [backface-visibility:hidden] [transform:rotateY(180deg)]">
              <span className="absolute left-5 top-5 inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-primary-foreground/80">
                <span
                  className="size-1.5 rounded-full bg-primary-foreground/80"
                  aria-hidden="true"
                />
                Answer
              </span>
              <span className="max-w-prose text-2xl font-semibold leading-snug sm:text-3xl">
                <MathText text={card.back} />
              </span>
              <span className="absolute bottom-5 right-5 inline-flex items-center gap-1.5 text-xs font-medium text-primary-foreground/80">
                <RotateCw className="size-3.5" aria-hidden="true" />
                Tap to flip back
              </span>
            </span>
          </button>
        </div>

        {/* progress + controls */}
        <div className="space-y-3">
          <div className="flex items-center justify-between text-xs font-medium text-muted-foreground tabular-nums">
            <span>Tap the card (or press Space) to flip</span>
            <span>{pct}% through</span>
          </div>
          <div
            className="h-2 overflow-hidden rounded-full bg-muted"
            role="progressbar"
            aria-valuenow={pos + 1}
            aria-valuemin={1}
            aria-valuemax={total}
            aria-label="Study progress"
          >
            <div
              className="h-full rounded-full bg-primary transition-[width] duration-500"
              style={{ width: `${pct}%` }}
            />
          </div>
          <div className="flex items-center justify-between gap-3">
            <Button
              variant="outline"
              className="press min-h-touch flex-1"
              onClick={() => go(-1)}
              disabled={pos === 0}
              data-testid="study-prev"
            >
              <ChevronLeft aria-hidden="true" />
              Previous
            </Button>
            <Button
              variant="outline"
              className="press min-h-touch flex-1"
              onClick={() => go(1)}
              disabled={pos >= total - 1}
              data-testid="study-next"
            >
              Next
              <ChevronRight aria-hidden="true" />
            </Button>
          </div>
        </div>
      </div>

      {/* ---- session rail: deck map ---- */}
      <Card className="card-soft">
        <CardContent className="space-y-4 p-5">
          <div className="flex items-center gap-2 text-sm font-semibold tracking-tight">
            <Layers className="size-4 text-primary" aria-hidden="true" />
            Deck map
          </div>
          <dl className="space-y-px text-sm">
            <div className="flex items-center justify-between border-b py-2">
              <dt className="text-muted-foreground">Total cards</dt>
              <dd className="font-semibold tabular-nums">{total}</dd>
            </div>
            <div className="flex items-center justify-between border-b py-2">
              <dt className="text-muted-foreground">Current</dt>
              <dd className="font-semibold tabular-nums">{pos + 1}</dd>
            </div>
            <div className="flex items-center justify-between py-2">
              <dt className="text-muted-foreground">Remaining</dt>
              <dd className="font-semibold tabular-nums">{total - pos - 1}</dd>
            </div>
          </dl>
          <div>
            <p className="mb-2 text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
              Jump to card
            </p>
            <div className="flex flex-wrap gap-1.5" role="list" aria-label="Cards in this deck">
              {order.map((_, i) => (
                <button
                  key={i}
                  type="button"
                  role="listitem"
                  onClick={() => goTo(i)}
                  aria-label={`Go to card ${i + 1}`}
                  aria-current={i === pos ? 'true' : undefined}
                  className={cn(
                    'press flex size-7 items-center justify-center rounded-md border text-xs font-semibold tabular-nums transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                    i === pos
                      ? 'border-primary bg-primary text-primary-foreground'
                      : 'border-border bg-muted/40 text-muted-foreground hover:border-primary hover:text-primary',
                  )}
                >
                  {i + 1}
                </button>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
